import React, { useState, useMemo, useCallback, useEffect } from 'react'
import 'react-hint/css/index.css'
import { GetSettings } from 'components/requests/GetSettingsRequest'
import { pathOr } from 'rambdax'
import { AppProviders } from 'AppProviders'
import Routes from 'routes'
import { AppLock } from 'AppLock'
import { StoreUrlParams } from 'StoreUrlParams'
import { AuthLock } from 'authentication-lock'
import { BoardsIOSBridgeProvider } from 'components/contexts/boards-ios/js-bridge'
import {
    getLocalStorageItem,
    getSessionStorageItem,
    removeSessionStorageItem,
    removeLocalStorageItem,
    setLocalStorageItem,
    setSessionStorageItem
} from 'businesslayer/minutesLocalStore'
import LinearIndeterminate from 'components/shared/LinearProgress'
import emitter from 'common/util/events'
import { doLogin } from 'businesslayer/sso/SsoManagerFactoryService'
import { getSearchParameter } from 'components/helpers/DomHelper'
import Cookies from 'js-cookie'
import {
    getTemplateServiceToken,
    startTokenRenewal,
    stopTokenRenewal
} from 'businesslayer/networkrequests'
import ssoConfig from './config/ssoConfig.json'

const Loading = ({ isLoading }) => <LinearIndeterminate loading={isLoading} />

const isBoardLogin = () => {
    const platform = getSessionStorageItem('parentPlatform')
    const result =
        platform === 'boardswebadmin' ||
        platform === 'boardswebdirector' ||
        platform === 'boardsios'
    return result
}

const isOidcEnabledBoard = () => {
    const { _env_ } = window
    return _env_.ENABLE_OIDC_LOGIN_BOARD
}
const getUrlPath = (): string => {
    const url = window.location.href
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    return pathname
}

const isBoardEffect = (platform: any) => {
    if (process.env.NODE_ENV === 'development') {
        return true
    }
    if (platform !== 'boardswebadmin' && platform !== 'boardswebdirector') {
        let initialAuthToken = Cookies.get('authToken')
        if (initialAuthToken) {
            setSessionStorageItem('forbidden', 'false')
            return true
        } else {
            return false
        }
    }
    return false
}

const clearSessions = () => {
    // Preserve landingPath for proper redirection after authentication
    const currentLandingPath = getLocalStorageItem('landingPath')

    removeSessionStorageItem('committeeId')
    removeSessionStorageItem('committeeName')
    removeSessionStorageItem('siteName')
    removeSessionStorageItem('bookId')
    removeSessionStorageItem('parentPlatform')
    removeSessionStorageItem('sessionId')
    removeSessionStorageItem('landingPath')
    removeSessionStorageItem('uuid')
    removeSessionStorageItem('isNotification')

    // Restore landingPath if it was set
    if (currentLandingPath) {
        setLocalStorageItem('landingPath', currentLandingPath)
    }
}

const App = () => {
    const [isLoading, setIsLoading] = useState(true)
    const authtoken = getSearchParameter(window.location, ['authToken', 'authtoken'])
    const [isEnvAndTokenMatch, setIsEnvAndTokenMatch] = useState(false)
    const uuid = sessionStorage.getItem('uuid')
    const sessionId = getLocalStorageItem('sessionId')
    const { _env_ } = window

    const SSO_AUTHORITY_URL =
        process.env.NODE_ENV !== 'development' ? _env_.SSO_AUTHORITY_URL : ssoConfig.authority
    if (process.env.NODE_ENV === 'development' && !sessionId) {
        doLogin()
    }
    useEffect(() => {
        let url = new URL(window.location.href)
        const sessionId = getLocalStorageItem('sessionId')

        if (isBoardLogin() || process.env.NODE_ENV === 'development') {
            const params = new URLSearchParams()
            params.append('client_id', ssoConfig.minutes_clientId)
            params.append('grant_type', ssoConfig.template_doc_service_grantType)
            params.append('token', sessionId)
            params.append('scope', ssoConfig.template_doc_service_scope)
            // Get initial token and start renewal every 25 minutes
            getTemplateServiceToken(SSO_AUTHORITY_URL, params)
                .then(() => {
                    // Start token renewal every 25 minutes
                    startTokenRenewal(SSO_AUTHORITY_URL, params)
                })
                .catch((error) => {
                    console.error('App.tsx: Failed to get template service token:', error)
                })
        }
        if (sessionId) {
            const env = sessionId.split(':')[0].toLowerCase()
            const match = url.origin.toLowerCase().includes(env)
            setIsEnvAndTokenMatch(match)
        }

        // Cleanup on unmount
        return () => {
            stopTokenRenewal()
        }
    }, [isEnvAndTokenMatch, SSO_AUTHORITY_URL])
    if (authtoken) {
        setSessionStorageItem('authToken', 'true')
        clearSessions()
    }
    // Handle site switch
    let url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    if (params.get('token') === 'environmentRedirect') {
        const userLanguage = getSearchParameter(window.location, ['userLanguage', 'userLanguage'])
        if (userLanguage !== null) {
            setLocalStorageItem('userLanguage', userLanguage)
        }
        const platform = getSearchParameter(window.location, ['parentPlatform', 'parentplatform'])
        if (platform !== null) {
            setSessionStorageItem('parentPlatform', platform)
        }

        // Only set landingPath to "/" if it's not already set to a specific path
        const existingLandingPath = getLocalStorageItem('landingPath')
        if (!existingLandingPath) {
            setLocalStorageItem('landingPath', '/')
        }

        if (!uuid) {
            removeSessionStorageItem('sessionId')
            removeLocalStorageItem('sessionId')
        }
        doLogin()
    }
    // Changes for OIDC flow
    const platform = getSearchParameter(window.location, ['parentPlatform', 'parentplatform'])
    const boardEffectPlatform = isBoardEffect(platform)
    const isBoardsIOS = platform === 'boardsios'

    if (!isBoardEffect(platform)) {
        if (!!platform) {
            setSessionStorageItem('parentPlatform', platform)
        }
    }

    const isBoardLoginResult = isBoardLogin()

    if (isBoardLoginResult) {
        const bookId = getSearchParameter(window.location, ['bookid', 'bookId'])
        if (bookId !== null) {
            setSessionStorageItem('bookId', bookId)
        }

        const urlCommitteeId = getSearchParameter(window.location, ['committeeId', 'committeeid'])
        if (urlCommitteeId !== null) {
            setSessionStorageItem('committeeId', urlCommitteeId)
        }

        const committeeName = getSearchParameter(window.location, [
            'committeeName',
            'committeename'
        ])
        if (committeeName !== null) {
            setSessionStorageItem('committeeName', committeeName)
        }

        const siteName = getSearchParameter(window.location, ['siteName', 'sitename'])
        if (siteName !== null) {
            setSessionStorageItem('siteName', siteName)
        }

        const currentPath = getUrlPath()
        setLocalStorageItem('landingPath', currentPath)

        const userLanguage = getSearchParameter(window.location, ['userLanguage', 'userLanguage'])
        if (userLanguage !== null) {
            setLocalStorageItem('userLanguage', userLanguage)
        }
    } else {
        setSessionStorageItem('environmentRedirect', 'true')
    }

    const isOidcEnabled = isOidcEnabledBoard() === true || isOidcEnabledBoard() === 'true'
    const isLoginRequired = isBoardLoginResult && isOidcEnabled

    if (!isBoardsIOS && isLoginRequired && (authtoken || !sessionId)) {
        if (!uuid) {
            removeSessionStorageItem('sessionId')
            removeLocalStorageItem('sessionId')
        }
        doLogin()
    }

    // update landing path
    const sessionPlatform = useMemo(() => getSessionStorageItem('parentPlatform'), [])

    // For BoardEffect, also bypass environment matching on page refresh
    useEffect(() => {
        if (sessionPlatform === 'boardeffect') {
            // Check if we have a sessionId in sessionStorage for BoardEffect
            const sessionIdFromStorage = getSessionStorageItem('sessionId')
            if (sessionIdFromStorage) {
                setIsEnvAndTokenMatch(true)
            }
        } else if (!sessionPlatform && !platform) {
            const sessionIdFromStorage = getSessionStorageItem('sessionId')
            const localSessionId = getLocalStorageItem('sessionId')

            if (sessionIdFromStorage && !localSessionId) {
                setIsEnvAndTokenMatch(true)
            }
        }
    }, [platform, sessionPlatform])

    const handleLoadSuccess = useCallback(
        () =>
            setTimeout(() => {
                setIsLoading(false)
            }, 1000),
        []
    )

    // Hide global loader when app enters locked state (LockedModal handles its own loading feedback)
    useEffect(() => {
        const handleLocked = () => {
            setIsLoading(false)
        }
        emitter.on('locked', handleLocked)
        return () => {
            emitter.off('locked', handleLocked)
        }
    }, [])

    const forbidden = getSessionStorageItem('forbidden')
    const isForbidden = forbidden === 'true'

    return (
        <>
            {/* if isOidcEnabled is false then existing flow (authtoken) will work && isLoginRequired */}
            {authtoken && isLoginRequired ? null : (
                <StoreUrlParams>
                    {({ committeeId, authTokens, platform, uuid, siteName }) => (
                        <AppContent
                            committeeId={committeeId}
                            authTokens={authTokens}
                            platform={platform}
                            uuid={uuid}
                            siteName={siteName}
                            sessionPlatform={sessionPlatform}
                            handleLoadSuccess={handleLoadSuccess}
                            isEnvAndTokenMatch={isEnvAndTokenMatch}
                            forbidden={isForbidden}
                            boardEffectPlatform={boardEffectPlatform}
                        />
                    )}
                </StoreUrlParams>
            )}
            <Loading isLoading={isLoading} />
        </>
    )
}

// Component that handles the core app content
const AppContent = ({
    committeeId,
    authTokens,
    platform,
    uuid,
    siteName,
    sessionPlatform,
    handleLoadSuccess,

    isEnvAndTokenMatch,
    forbidden,
    boardEffectPlatform
}) => (
    <BoardsIOSBridgeProvider platform={platform || sessionPlatform}>
        <AuthLock
            platform={platform || sessionPlatform}
            uuid={uuid}
            authTokens={authTokens}
            siteName={siteName}>
            {((!forbidden && isEnvAndTokenMatch) || boardEffectPlatform) && (
                <GetSettings>
                    {({ settings }) => (
                        <AppProviders
                            platform={settings.platform}
                            language={pathOr('en-us', ['userLanguage'], settings)}
                            committeeId={committeeId || ''}>
                            <AppLock
                                platform={settings.platform}
                                onLoadedSuccessful={handleLoadSuccess}>
                                <Routes />
                            </AppLock>
                        </AppProviders>
                    )}
                </GetSettings>
            )}
        </AuthLock>
    </BoardsIOSBridgeProvider>
)

// Fullscreen loading spinner

export default App
